import { AcumaticaService } from './../acumatica/acumatica.service';
import { BuildDetailsHttpService } from './build-details-http.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, timer } from 'rxjs';
import { HttpHeaders, HttpClient } from '@angular/common/http';
import { BuildDetails } from '../models/BuildDetails';
import { environment } from 'src/environments/environment';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialog } from '../common/alert-dialog/alert-dialog.component';

@Injectable({ providedIn: 'root' })
export class UpToDateBuildService {

    // BehaviorSubject: Requires an initial value and emits the current value to new subscribers
    //      https://www.learnrxjs.io/learn-rxjs/subjects/behaviorsubject
    private buildIsUpToDateSubject = new BehaviorSubject<boolean>(true);
    public get buildIsUpToDate(): BehaviorSubject<boolean> {
        return this.buildIsUpToDateSubject;
    }
    private _buildNumberAtStartup: string = '';
    public get buildNumberAtStartup(): string { return this._buildNumberAtStartup; }
    private _buildVersionMessage: string = "An updated version of the application is available\nPlease Refresh your browser page";
    public get buildVersionMessage(): string { return this._buildVersionMessage; }

    // On-the-fly Configurations
    private componentLoggingSet = {
        OrderPicking: new BehaviorSubject<BackendLoggingLevel>(BackendLoggingLevel.None),
        TransferPutaway: new BehaviorSubject<BackendLoggingLevel>(BackendLoggingLevel.None)
    };
    public get componentLogging() { return this.componentLoggingSet; }

    public username: string | null = null;

    constructor(
        private httpClient: HttpClient,
        private buildDetailsHttpService: BuildDetailsHttpService,
        private dialog: MatDialog,
    ) {
        this.buildDetailsHttpService.fetchBuildDetails().then(details => {
            this._buildNumberAtStartup = details.buildNumber!;

            const localStorageKey = 'last-update-message-' + environment.mode;
            const lastUserUpdateMessage = localStorage.getItem(localStorageKey);

            const message = environment.mode == 'wms'
                ? details.WMSVersionMessage
                : details.RepVersionMessage;
            if (message) this._buildVersionMessage = message;

            if (lastUserUpdateMessage != this._buildNumberAtStartup) {
                if (message) {
                    this.dialog.open(AlertDialog, { data: { message: message, buttonText: "OK" } }).afterClosed().subscribe(() => {
                        localStorage.setItem(localStorageKey, this._buildNumberAtStartup);
                    });
                }
            }
        })
        this.pollForBuildInfo();
    }

    private pollForBuildInfo() {

        const pollInterval = 1000 * 60 * 5; // 5 minutes

        const httpOptions = {
            headers: new HttpHeaders({
                'Cache-Control': 'no-cache'
            })
        };

        timer(0, pollInterval).subscribe(() => {
            this.httpClient.get<BuildDetails>('assets/build-details.json', httpOptions).subscribe(
                response => {
                    // Build #
                    const newBuildNumber = response.buildNumber;
                    if (this.buildNumberAtStartup !== newBuildNumber) {
                        this.buildIsUpToDateSubject.next(false);
                    }

                    let userSystemWideLogging = response.backendLogging._;

                    Object.keys(this.componentLoggingSet).forEach(compID => {
                        let compLogging: any = (<any>response.backendLogging)[compID];
                        let compByUser = compLogging?.User;
                        let level =
                            // component and user specific indication
                            (compByUser && this.username && compByUser[this.username.toUpperCase()])
                            // user default
                            || (userSystemWideLogging && this.username && (<any>userSystemWideLogging)[this.username])
                            // component default
                            || (compLogging && compLogging.Level)
                            // finally
                            || BackendLoggingLevel.None;

                        // on-the-fly configuration changes
                        if (level !== (<any>this.componentLoggingSet)[compID].value) {
                            (<any>this.componentLoggingSet)[compID].next(level);
                        }
                    });
                },
                error => {
                    // quietly handle failures to retrieve build-details.json
                    console.error(error);
                }
            );
        });
    }
}

export enum BackendLoggingLevel {
    None = 0,
    AcumaticaAPIs = 1,
    Extensive = 2
}